import { useState } from "react";
import {
  DateTimeField,
  EmptyState,
  Page,
  Badge,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import {
  useTemplateVersions,
  type TemplateKind,
  type TemplateVersion,
} from "../../lib/queries/templates";
import { VersionPreviewModal } from "./VersionPreviewModal";

interface VersionsSectionProps {
  templateId: string;
  kind: TemplateKind;
  /** The template's channel; an SMS version has only a text body. */
  channel: "email" | "sms";
  /** Parsed sample data used to render historic versions in the modal. */
  sampleData: unknown;
}

/** Version history table with a read-only preview modal per version. */
export function VersionsSection({ templateId, kind, channel, sampleData }: VersionsSectionProps) {
  const versions = useTemplateVersions(templateId);
  const [viewing, setViewing] = useState<TemplateVersion | null>(null);

  const columns: Column<TemplateVersion>[] = [
    {
      key: "version",
      header: "Version",
      numeric: true,
      render: (row) => <Text variant="mono">{row.version}</Text>,
    },
    {
      key: "subject",
      header: "Subject",
      primary: true,
      render: (row) => row.subject_tmpl || <Text variant="muted">—</Text>,
    },
    {
      key: "published",
      header: "Status",
      render: (row) =>
        row.published ? (
          <Badge weight="ghost" variant="ok" dot>Published</Badge>
        ) : (
          <Badge weight="ghost" variant="warn" dot>Draft</Badge>
        ),
    },
    {
      key: "published_at",
      header: "Published at",
      render: (row) =>
        row.published_at ? (
          <Text variant="mono">
            <DateTimeField record={row} source="published_at" />
          </Text>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action onClick={() => setViewing(row)}>View</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      <Page.SectionHeader title="Version history" />
      <Table<TemplateVersion>
        striped
        cards
        columns={columns}
        rows={versions.data ?? []}
        rowKey={(r) => r.id}
        isLoading={versions.isLoading}
        error={versions.error}
        onRetry={versions.refetch}
        empty={
          <EmptyState
            title="No versions"
            description="The template has no versions yet."
          />
        }
      />

      {viewing && (
        <VersionPreviewModal
          version={viewing}
          kind={kind}
          channel={channel}
          sampleData={sampleData}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
